import { anim, elem } from 'modapp-utils';
import { Model, Collection } from 'modapp-resource';
import { Txt, Button, Elem, Html, Transition, Input } from 'modapp-base-component';
import { CollectionList, ModelComponent } from 'modapp-resource-component';
import ResClient, { isResError } from 'resclient';
import 'reset-css';
import 'css/style.css';

// Creating the client instance.
global.client = new ResClient('ws://localhost:8080');
let eventBus = client.eventBus;

class WaitOverlay {
	constructor(div, size = 'small', animate = true) {
		if (typeof size == 'boolean') {
			animate = size;
			size = 'small';
		}
		this.overlay = elem.create('div', Object.assign({}, { className: 'comp-waitoverlay' }));
		this.loader = elem.create('div', { className: 'comp-waitoverlay-loader ' + size });
		elem.append(this.loader, elem.create('div'));
		this.overlayAnimId = null;
		this.layoutAnimId = null;
		elem.append(div, this.overlay);
		elem.append(div, this.loader);
		if (animate) {
			var overlayOpac = window.getComputedStyle(this.overlay).getPropertyValue('opacity');
			var loaderOpac = window.getComputedStyle(this.loader).getPropertyValue('opacity');
			this.overlay.style.opacity = '0';
			this.loader.style.opacity = '0';
			this.overlayAnimId = anim.fade(this.overlay, overlayOpac);
			this.layoutAnimId = anim.fade(this.loader, loaderOpac);
		}
	}
	close(animate = true) {
		if (!this.overlay) return;
		animate = typeof animate == 'boolean' ? animate : true;
		if (animate) {
			anim.stop(this.overlayAnimId);
			anim.stop(this.layoutAnimId);
			anim.fade(this.loader, 0);
			anim.fade(this.overlay, 0, { callback: this._remove.bind(this) });
		} else {
			this._remove();
		}
	}
	_remove() {
		elem.remove(this.overlay);
		elem.remove(this.loader);
		this.overlay = null;
		this.loader = null;
	}
}

class ComponentLoader {
	constructor(componentPromise) {
		this.div = null;
		this.componentPromise = null;
		this.renderedComponent = null;
		this.overlay = null;
		this.animId = null;
		this.setComponent(componentPromise);
	}
	render(div) {
		if (this.div) throw "Already rendered";
		this.div = elem.create('div', { className: 'comp-componentLoader' });
		elem.append(div, this.div);
		if (this.componentPromise) {
			if (this._isPending(this.componentPromise)) {
				this.overlay = new WaitOverlay(this.div, 'medium', false);
			}
			Promise.resolve(this.componentPromise)
				.then(function(oldComponent, component) {
					if (!this.div) return;
					if (oldComponent !== this.componentPromise) return;
					if (this.overlay) {
						this.overlay.close();
						this.overlay = null;
						let el = component.render(this.div);
						el.style.opacity = '0';
						this.animId = anim.fade(el, 1);
					} else {
						component.render(this.div);
					}
					this.renderedComponent = component;
				}.bind(this, this.componentPromise))
				.catch(e => {
					if (this.overlay) {
						this.overlay.close();
					}
					throw e;
				});
		}
		return this.div;
	}
	unrender() {
		if (!this.div) return;
		if (this.overlay) {
			this.overlay.close(false);
		}
		anim.stop(this.animId);
		this.animId = null;
		if (this.renderedComponent) {
			this.renderedComponent.unrender();
			this.renderedComponent = null;
		}
		elem.empty(this.div);
		this.div = null;
	}
	setComponent(componentPromise) {
		if (componentPromise === this.componentPromise) {
			return this;
		}
		if (!this.div) {
			this.componentPromise = componentPromise;
			return this;
		}
		throw "Not implemented";
	}
	_isPending(promise) {
		let pending = true;
		Promise.resolve(promise).then(() => pending = false, () => pending = false);
		return pending;
	}
}

class ViewerModelValueComponent {
	constructor(prop, model, path) {
		this.prop = prop;
		this.model = model;
		this.path = path;
		this._onModelChange = this._onModelChange.bind(this);
		this.elem = null;
	}
	render(el) {
		this.elem = new Elem(n =>
			n.elem('div', { className: 'module-viewer--value' }, [
				n.elem('div', { className: 'head' }, [
					n.component(new Txt(this.prop)),
					n.component('head', new Transition())
				]),
				n.elem('div', { className: 'body' }, [
					n.component('body', new Transition())
				])
			])
		);
		this._setValue();
		this._setEventListener(true);
		return this.elem.render(el);
	}
	unrender() {
		this._setEventListener(false);
		this.elem.unrender();
		this.elem = null;
	}
	_setEventListener(on) {
		if (on) {
			this.model.on('change', this._onModelChange);
		} else {
			this.model.off('change', this._onModelChange);
		}
	}
	_onModelChange(changed) {
		if (!changed.hasOwnProperty(this.prop)) {
			return;
		}
		this._setValue();
	}
	_setValue() {
		if (!this.elem) {
			return;
		}
		let v = this.model[this.prop];
		let h, b = null;
		if (typeof v === 'object' && v !== null) {
			h = null;
			b = new ViewerResourceComponent(v, this.path);
		} else {
			let typ = v === null ? 'null' : typeof v;
			h = new Txt(String(v), { className: 'module-viewer--' + typ });
			b = null;
		}
		this.elem.getNode('head').fade(h);
		this.elem.getNode('body').fade(b);
	}
}

class ViewerResourceComponent {
	constructor(resource, path = []) {
		this.resource = resource;
		if (typeof resource.getResourceId === 'function') {
			let rid = resource.getResourceId();
			this.cyclic = path.indexOf(rid) >= 0;
			this.path = path.slice();
			this.path.push(rid);
		}
		this.modelCollection = null;
		this.elem = null;
	}
	render(el) {
		let c;
		if (this.cyclic) {
			c = this._cyclicComponent();
		} else if (this._isError()) {
			c = this._errorComponent();
		} else if (this._isCollection()) {
			c = this._collectionComponent();
		} else {
			c = this._modelComponent();
		}
		this.elem = c;
		return this.elem.render(el);
	}
	_isError() {
		return isResError(this.resource);
	}
	_isCollection(r) {
		return this.resource === null
			? false
			: typeof this.resource[Symbol.iterator] === 'function';
	}
	_errorComponent() {
		return new Elem(n => n.elem('div', { className: 'module-viewer--error' }, [
			n.component(new Txt(this.resource.getResourceId(), { className: 'module-viewer--resource' })),
			n.component(new Txt(this.resource.message, { className: 'module-viewer--error-message' })),
		]));
	}
	_cyclicComponent() {
		return new Elem(n => n.elem('div', { className: 'module-viewer--cycle' }, [
			n.component(new Txt(this.resource.getResourceId() + " (cycle)", { className: 'module-viewer--resource' })),
		]));
	}
	_collectionComponent() {
		return new Elem(n => n.elem('div', { className: 'module-viewer--collection' }, [
			n.component(new Txt(this.resource.getResourceId(), { className: 'module-viewer--resource' })),
			n.component(new CollectionList(this.resource, v => {
				if (typeof v === 'object' && v !== null) {
					return new ViewerResourceComponent(v, this.path);
				}
				let typ = v === null ? 'null' : typeof v;
				return new Txt(String(v), { className: 'module-viewer--' + typ });
			}, { eventBus }))
		]));
	}
	_modelComponent() {
		let collection = this._createModelCollection();
		let cb = this._handleModelChange.bind(this, collection);
		this.resource.on('change', cb);
		this.onUnrender = () => this.resource.off('change', cb);
		return new Elem(n => n.elem('div', { className: 'module-viewer--model' }, [
			n.elem('div', [
				n.component(new Txt(this.resource.getResourceId(), { className: 'module-viewer--resource' })),
				n.component(new CollectionList(collection, m => new ViewerModelValueComponent(m.id, this.resource, this.path)))
			])
		]));
	}
	_createModelCollection() {
		let data = [];
		Object.keys(this.resource).forEach(id => {
			if (this.resource.hasOwnProperty(id) && id.substr(0, 1) !== '_') {
				data.push({ id });
			}
		});
		return new Collection({
			data,
			compare: (a, b) => a.id.localeCompare(b.id),
			eventBus
		});
	}
	_handleModelChange(collection, change) {
		if (!this.elem) {
			return;
		}
		for (let id in change) {
			if (change[id] === undefined) {
				collection.add({ id });
			} else if (this.resource[id] === undefined) {
				collection.remove(id);
			}
		}
	}
	unrender() {
		if (this.onUnrender) {
			this.onUnrender();
			this.onUnrender = null;
		}
		this.elem.unrender();
		this.elem = null;
	}
}

class ViewerComponent {
	constructor(client, model) {
		this.client = client;
		this.model = model;
		this.loadedRID = null;
		this.elem = null;
		this.instructionComponent = new Html(
			`<p>Enter a resource ID to view it together with all linked resources.</p><p>The viewer connects to Resgate at <code>localhost:8080</code>.</p>`,
			{ tagName: 'div' }
		);
	}
	render(el) {
		this.elem = new Elem(n =>
			n.elem('div', { className: 'module-viewer' }, [
				n.elem('div', { className: 'top' }, [
					n.elem('div', { className: 'rid-container' }, [
						n.component(new ModelComponent(
							this.model,
							new Input(this.model.rid, {
								events: {
									input: c => this.model.set({ rid: c.getValue() }),
									keydown: (c, e) => {
										if (e.keyCode == 13) {
											this._clickView();
										}
									}
								},
								attributes: {
									id: 'resource-id',
									placeholder: `Resource ID`
								}
							}),
							(m, c) => {
								c.setValue(m.rid);
							}
						)),
						n.component(new ModelComponent(
							this.model,
							new Button(`View`, () => this._clickView()),
							(m, c) => c.setDisabled(!m.rid)
						)),
					]),
				]),
				n.elem('hr'),
				n.elem('div', { className: 'resource-container' }, [
					n.component('view', new Transition().set(this.instructionComponent))
				])
			])
		);
		this._clickView();
		return this.elem.render(el);
	}
	unrender() {
		this.elem.unrender();
		this.elem = null;
		this.loadedRID = null;
	}
	_clickView(force) {
		let rid = this.model.rid.trim() || null;
		this.loadedRID = rid;
		let view = this.elem.getNode('view');
		if (!rid) {
			view.fade(this.instructionComponent);
			return;
		}
		view.fade(new ComponentLoader(this.client.get(rid)
			.catch(err => err)
			.then(resource => new Elem(n => n.elem('div', [
				n.elem('p', [
					n.elem('a', { attributes: { href: getWebResourceUri(rid), target: '_blank' }}, [
						n.component(new Txt(getWebResourceUri(rid)))
					]),
				]),
				n.component(new ViewerResourceComponent(resource))
			])))
		));
	}
}

function getWebResourceUri(rid) {
	let idx = rid.indexOf('?');
	let rname = idx >= 0 ? rid.substr(0, idx) : rid;
	let query = idx >= 0 ? rid.substr(idx) : '';
	return "http://localhost:8080/api/" + rname.replace(/\./g, '/') + query;
}

let model = new Model({
	data: {
		rid: ''
	},
	eventBus
});

new ViewerComponent(client, model).render(document.getElementById('root'));
